import { useState, useEffect, useRef } from "react";
import {
  Box, Button, Typography, Paper, Grid, TablePagination, TextField,
  ToggleButtonGroup, ToggleButton, Chip,
} from "@mui/material";
import SearchBar from "../components/common/SearchBar";
import { lotsApi } from "../services/api";
import useCrudForm from "../hooks/useCrudForm";
import { APP_PALETTE } from "../theme/palette";
import { formatCurrencyBs } from "../utils/formatters";

export default function LotsPage() {
  const [lots, setLots] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const formRef = useRef(null);

  const load = () => lotsApi.list().then((r) => setLots(r.data));
  useEffect(() => { load(); }, []);

  const {
    showForm,
    editing,
    form,
    setForm,
    toggleCreate,
    openEdit,
    submitForm,
  } = useCrudForm({
    initialForm: {
      name: "", brand: "", purchase_currency: "BS",
      total_units: "", total_cost: "", total_cost_usd: "", bs_portion_amount: "", exchange_rate: "",
      notes: "",
    },
    loadData: load,
    createItem: (payload) => lotsApi.create(payload),
    updateItem: (id, payload) => lotsApi.update(id, payload),
    mapToForm: (l) => ({
      name: l.name,
      brand: l.brand,
      purchase_currency: l.purchase_currency || "BS",
      total_units: String(l.total_units),
      total_cost: String(l.total_cost),
      total_cost_usd: l.total_cost_usd != null ? String(l.total_cost_usd) : "",
      bs_portion_amount: l.bs_portion_amount != null ? String(l.bs_portion_amount) : "",
      exchange_rate: l.exchange_rate != null ? String(l.exchange_rate) : "",
      notes: l.notes || "",
    }),
    mapToPayload: (payload) => {
      const base = {
        name: payload.name,
        brand: payload.brand,
        total_units: parseInt(payload.total_units, 10),
        purchase_currency: payload.purchase_currency,
        notes: payload.notes,
      };
      if (payload.purchase_currency === "USD") {
        base.total_cost_usd = parseFloat(payload.total_cost_usd);
        base.exchange_rate = parseFloat(payload.exchange_rate);
      } else if (payload.purchase_currency === "MIXED") {
        base.total_cost_usd = parseFloat(payload.total_cost_usd);
        base.bs_portion_amount = parseFloat(payload.bs_portion_amount);
        base.exchange_rate = parseFloat(payload.exchange_rate);
      } else {
        base.total_cost = parseFloat(payload.total_cost);
      }
      return base;
    },
  });

  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [showForm, editing]);

  useEffect(() => {
    if (!showForm) {
      setSubmitAttempted(false);
    }
  }, [showForm]);

  const filtered = lots.filter((l) =>
    !search ||
    l.name.toLowerCase().includes(search.toLowerCase()) ||
    l.brand.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => b.id - a.id);

  const isUsd = form.purchase_currency === "USD";
  const isMixed = form.purchase_currency === "MIXED";

  const mixedTotalBs =
    isMixed && form.bs_portion_amount && form.total_cost_usd && form.exchange_rate
      ? parseFloat(form.bs_portion_amount) + (parseFloat(form.total_cost_usd) * parseFloat(form.exchange_rate))
      : null;

  const unitCostPreview = isMixed
    ? (form.total_units && mixedTotalBs != null
        ? `${formatCurrencyBs(mixedTotalBs / parseInt(form.total_units))} por unidad`
        : "—")
    : isUsd
      ? (form.total_units && form.total_cost_usd
          ? `$us ${(parseFloat(form.total_cost_usd) / parseInt(form.total_units)).toFixed(2)} por unidad`
          : "—")
      : (form.total_units && form.total_cost
          ? `${formatCurrencyBs(parseFloat(form.total_cost) / parseInt(form.total_units))} por unidad`
          : "—");

  const bsEquivalentPreview =
    isUsd && form.total_cost_usd && form.exchange_rate
      ? formatCurrencyBs(parseFloat(form.total_cost_usd) * parseFloat(form.exchange_rate))
      : isMixed && mixedTotalBs != null
        ? formatCurrencyBs(mixedTotalBs)
        : null;

  const handleSubmit = () => {
    setSubmitAttempted(true);
    submitForm();
  };

  const isSubmitDisabled =
    !form.name || !form.brand || !form.total_units ||
    (isUsd
      ? (!form.total_cost_usd || !form.exchange_rate)
      : isMixed
        ? (!form.bs_portion_amount || !form.total_cost_usd || !form.exchange_rate)
        : !form.total_cost);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", sm: "center" }, flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 3 }}>
        <Typography variant="h5" fontWeight={700} color={APP_PALETTE.text.primary}>Lotes de mercancía</Typography>
        <Button variant="contained"
          onClick={toggleCreate}
          sx={{ width: { xs: "100%", sm: "auto" }, background: APP_PALETTE.brand.primary, "&:hover": { background: APP_PALETTE.brand.primaryHover }, borderRadius: 2 }}>
          {showForm ? "Cancelar" : "+ Nuevo lote"}
        </Button>
      </Box>

      {showForm && (
        <Paper ref={formRef} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.08)" }}>
          <Typography variant="h6" fontWeight={600} mb={2}>{editing ? "Editar lote" : "Registrar lote"}</Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              Moneda de compra
            </Typography>
            <ToggleButtonGroup
              value={form.purchase_currency}
              exclusive
              size="small"
              onChange={(_, value) => value && setForm({ ...form, purchase_currency: value })}
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 2,
                  "&.Mui-selected": {
                    backgroundColor: APP_PALETTE.brand.soft,
                    color: APP_PALETTE.brand.primary,
                    fontWeight: 600,
                  },
                },
              }}
            >
              <ToggleButton value="BS">Bolivianos (Bs)</ToggleButton>
              <ToggleButton value="USD">Dólares (USD)</ToggleButton>
              <ToggleButton value="MIXED">Mixto (Bs + USD)</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={8}>
              <TextField label={submitAttempted ? "Nombre del lote *" : "Nombre del lote"} size="small" fullWidth
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Zara Enero 2026" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label={submitAttempted ? "Marca *" : "Marca"} size="small" fullWidth
                value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Ej: Zara" />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField label={submitAttempted ? "Unidades totales *" : "Unidades totales"} type="number" size="small" fullWidth inputProps={{ min: 1 }}
                value={form.total_units} onChange={(e) => setForm({ ...form, total_units: e.target.value })} placeholder="100" />
            </Grid>

            {isUsd ? (
              <>
                <Grid item xs={6} sm={3}>
                  <TextField label={submitAttempted ? "Costo total (USD) *" : "Costo total (USD)"} type="number" size="small" fullWidth inputProps={{ step: 0.01 }}
                    value={form.total_cost_usd} onChange={(e) => setForm({ ...form, total_cost_usd: e.target.value })} placeholder="2000" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField label={submitAttempted ? "Tipo de cambio (Bs) *" : "Tipo de cambio (Bs)"} type="number" size="small" fullWidth inputProps={{ step: 0.0001 }}
                    value={form.exchange_rate} onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })} placeholder="12.00" />
                </Grid>
              </>
            ) : isMixed ? (
              <>
                <Grid item xs={6} sm={4}>
                  <TextField label={submitAttempted ? "Monto pagado en Bs *" : "Monto pagado en Bs"} type="number" size="small" fullWidth inputProps={{ step: 0.01 }}
                    value={form.bs_portion_amount} onChange={(e) => setForm({ ...form, bs_portion_amount: e.target.value })} placeholder="5000" />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <TextField label={submitAttempted ? "Monto pagado en USD *" : "Monto pagado en USD"} type="number" size="small" fullWidth inputProps={{ step: 0.01 }}
                    value={form.total_cost_usd} onChange={(e) => setForm({ ...form, total_cost_usd: e.target.value })} placeholder="500" />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField label={submitAttempted ? "Tipo de cambio (Bs) *" : "Tipo de cambio (Bs)"} type="number" size="small" fullWidth inputProps={{ step: 0.0001 }}
                    value={form.exchange_rate} onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })} placeholder="12.00" />
                </Grid>
              </>
            ) : (
              <Grid item xs={6} sm={3}>
                <TextField label={submitAttempted ? "Costo total (Bs.) *" : "Costo total (Bs.)"} type="number" size="small" fullWidth inputProps={{ step: 0.01 }}
                  value={form.total_cost} onChange={(e) => setForm({ ...form, total_cost: e.target.value })} placeholder="20000" />
              </Grid>
            )}

            <Grid item xs={12} sm={isUsd || isMixed ? 12 : 6}>
              <Box sx={{ p: 1.5, background: APP_PALETTE.brand.soft, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography fontSize={14} fontWeight={600} color={APP_PALETTE.brand.primary}>
                  Costo unitario estimado: {unitCostPreview}
                </Typography>
                {bsEquivalentPreview && (
                  <Typography fontSize={12} color="text.secondary" mt={0.25}>
                    Equivalente total en Bs: {bsEquivalentPreview}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField label="Notas" size="small" fullWidth multiline rows={2}
                value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Observaciones del lote..." />
            </Grid>
          </Grid>
          <Button variant="contained" onClick={handleSubmit}
            disabled={isSubmitDisabled}
            sx={{ width: { xs: "100%", sm: "auto" }, background: APP_PALETTE.brand.primary, "&:hover": { background: APP_PALETTE.brand.primaryHover }, borderRadius: 2 }}>
            {editing ? "Guardar cambios" : "Registrar lote"}
          </Button>
        </Paper>
      )}

      <SearchBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(0);
        }}
        resultCount={filtered.length}
        onClear={() => {
          setSearch("");
          setPage(0);
        }}
      />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((l) => {
          const profitColor = l.profit >= 0 ? APP_PALETTE.status.success : APP_PALETTE.status.error;
          const pctSold = l.total_units > 0 ? Math.min(100, Math.round((l.units_sold / l.total_units) * 100)) : 0;
          const lotIsUsd = l.purchase_currency === "USD";
          const lotIsMixed = l.purchase_currency === "MIXED";
          return (
            <Paper key={l.id} sx={{ p: 3, borderRadius: 3, boxShadow: "0 1px 8px rgba(0,0,0,0.08)" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: { xs: "stretch", sm: "flex-start" }, flexDirection: { xs: "column", sm: "row" }, gap: 1.25, mb: 2 }}>
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography fontWeight={700} fontSize={16} color={APP_PALETTE.text.primary}>{l.name}</Typography>
                    {(lotIsUsd || lotIsMixed) && (
                      <Chip
                        label={lotIsMixed ? `Mixto @ ${Number(l.exchange_rate).toFixed(2)}` : `USD @ ${Number(l.exchange_rate).toFixed(2)}`}
                        size="small"
                        sx={{ background: APP_PALETTE.surfaces.infoSoft, color: APP_PALETTE.brand.primary, fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">Marca: <strong>{l.brand}</strong></Typography>
                  {l.notes && <Typography variant="caption" color="text.secondary" display="block">{l.notes}</Typography>}
                </Box>
                <Button variant="outlined" size="small" onClick={() => openEdit(l)}
                  sx={{ width: { xs: "100%", sm: "auto" }, color: APP_PALETTE.brand.primary, borderColor: APP_PALETTE.surfaces.brandBorderSoft, background: APP_PALETTE.brand.soft, borderRadius: 2, fontSize: 13 }}>
                  Editar
                </Button>
              </Box>

              <Grid container spacing={1.5} mb={2}>
                {[
                  { label: "Unidades totales", value: `${l.total_units} uds` },
                  { label: "Unidades vendidas", value: `${l.units_sold} uds`, color: APP_PALETTE.brand.primary },
                  { label: "Unidades restantes", value: `${l.units_remaining} uds`, color: l.units_remaining === 0 ? APP_PALETTE.status.error : APP_PALETTE.status.warning },
                  {
                    label: "Costo unitario",
                    value: (lotIsUsd || lotIsMixed) && l.unit_cost_usd != null
                      ? `$us ${Number(l.unit_cost_usd).toFixed(2)} (${formatCurrencyBs(l.unit_cost)})`
                      : formatCurrencyBs(l.unit_cost),
                    color: APP_PALETTE.text.muted,
                  },
                  { label: "Ingresos", value: formatCurrencyBs(l.total_revenue), color: APP_PALETTE.status.success },
                  { label: "Ganancia estimada", value: formatCurrencyBs(l.profit), color: profitColor },
                ].map((stat) => (
                  <Grid item xs={6} sm={4} md={2} key={stat.label}>
                    <Box sx={{ background: APP_PALETTE.surfaces.subtle, borderRadius: 2, p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block">{stat.label}</Typography>
                      <Typography fontWeight={700} fontSize={15} color={stat.color || APP_PALETTE.text.primary}>{stat.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Progreso de ventas</Typography>
                  <Typography variant="caption" fontWeight={600} color={APP_PALETTE.brand.primary}>{pctSold}%</Typography>
                </Box>
                <Box sx={{ background: APP_PALETTE.surfaces.borderSoft, borderRadius: 10, height: 8 }}>
                  <Box sx={{ width: `${pctSold}%`, background: APP_PALETTE.brand.primary, borderRadius: 10, height: 8, transition: "width 0.3s" }} />
                </Box>
              </Box>
            </Paper>
          );
        })}
        {filtered.length === 0 && (
          <Paper sx={{ p: 4, borderRadius: 3, textAlign: "center" }}>
            <Typography color="text.secondary">{search ? "No se encontraron lotes" : "No hay lotes registrados"}</Typography>
          </Paper>
        )}
      </Box>

      {filtered.length > rowsPerPage && (
        <TablePagination component="div" count={filtered.length} page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      )}
    </Box>
  );
}